import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Form, Input, message, Modal, Select, Spin, Switch, Tabs } from 'antd'
import { MinusCircleOutlined } from '@ant-design/icons'

import { ROLE_MAPPING_OPTIONS, USER_ROLES } from '../../../utils/user-utils'
import { createUser, fetchUser, updateUser } from '../../../services/user'
import { getProjectsAction, projectSelector } from '../../../store/projects'

import './EmployeeModal.css'

const { TabPane } = Tabs

const EmployeeModal = ({ showModal, userId, onSuccess, onClose }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()

  const projectFormValues = Form.useWatch('projects', form)

  const [isLoading, setIsLoading] = useState(false)
  const [changePassword, setChangePassword] = useState(false)

  const { projects } = useSelector(projectSelector)

  const projectOptions = useMemo(
    () =>
      projects
        ?.filter((project) => !projectFormValues?.some((selected) => selected.id === project.id))
        .map((item) => ({ value: item.id, label: item.attributes.name })),
    [projects, projectFormValues]
  )

  const clearData = () => {
    form.resetFields()
  }

  const onFormClose = () => {
    setChangePassword(false)
    clearData()
    onClose()
  }

  useEffect(() => {
    if (!projects || projects.length === 0) {
      dispatch(getProjectsAction({ pagination: {} }))
    }
  }, [dispatch, projects])

  useEffect(() => {
    if (showModal && userId) {
      setIsLoading(true)
      fetchUser(userId)
        .then(({ data }) => {
          if (showModal) {
            form.setFieldsValue({ ...data[0] })
          }
        })
        .catch((error) => {
          message.error(`Ошибка при загрузке пользователя ${userId}: ${error}`)
        })
        .finally(() => setIsLoading(false))
    }
  }, [userId, showModal, form])

  const onProjectSelect = (key, project) => {
    if (key) {
      const fields = form.getFieldsValue()

      fields.projects = [
        ...fields.projects,
        {
          id: key,
          name: project.label,
        },
      ]

      form.setFieldsValue(fields)
    }
  }

  const onFinish = (values) => {
    const role = USER_ROLES.find((role) => role.type === values.role?.type)
    const data = { ...values, role: role?.id }
    if (userId) {
      setIsLoading(true)
      updateUser(userId, data)
        .then(() => {
          if (onSuccess) {
            onSuccess()
          }
          clearData()
          message.success('Пользователь успешно обновлен')
        })
        .catch((error) => {
          message.error(`Ошибка при обновлении пользователя ${error}`)
          console.warn(error)
        })
        .finally(() => setIsLoading(false))
    } else {
      createUser(data)
        .then(() => {
          if (onSuccess) {
            onSuccess()
          }
          clearData()
          message.success('Пользователь успешно создан')
        })
        .catch((error) => {
          message.error(`Ошибка при создании пользователя ${error}`)
          console.warn(error)
        })
        .finally(() => setIsLoading(false))
    }
  }

  const initFields = useMemo(() => ({ role: {}, projects: [] }), [])

  return (
    <Modal
      title="Пользователь"
      width={1200}
      height={1200}
      visible={showModal}
      onCancel={onFormClose}
      forceRender
      footer={[
        !!userId && !changePassword && (
          <Button key="paswd" type="link" onClick={() => setChangePassword(true)} disabled={isLoading}>
            Сменить пароль
          </Button>
        ),
        <Button key="back" onClick={onFormClose} disabled={isLoading}>
          Отмена
        </Button>,
        <Button key="agree" type="primary" onClick={() => form.submit()} disabled={isLoading}>
          Сохранить
        </Button>,
      ]}
    >
      <Spin spinning={isLoading}>
        <div className="user-form-container">
          <Form initialValues={initFields} form={form} layout="vertical" onFinish={onFinish}>
            <Tabs>
              <TabPane tab="Атрибуты" key="attributes">
                <div className="user-display-block">
                  <div className="user-form-block">
                    <Form.Item
                      label="Пользователь"
                      name="username"
                      rules={[
                        { required: true, message: 'Пожалуйста введите логин пользователя' },
                        { min: 4, message: 'Минимум 4 символа' },
                      ]}
                    >
                      <Input disabled={!!userId} />
                    </Form.Item>

                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { required: true, message: 'Пожалуйста введите E-mail' },
                        { type: 'email', message: 'Введённый E-mail не корректный' },
                      ]}
                    >
                      <Input />
                    </Form.Item>

                    {(changePassword || !userId) && (
                      <Form.Item
                        label="Пароль"
                        name="password"
                        rules={[
                          { required: false, message: 'Пожалуйста введите пароль' },
                          { min: 6, message: 'Минимум 6 символов' },
                        ]}
                      >
                        <Input.Password />
                      </Form.Item>
                    )}
                    <Form.Item
                      label="Имя"
                      name="first_name"
                      rules={[{ required: true, message: 'Пожалуйста введите имя' }]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Фамилия"
                      name="second_name"
                      rules={[{ required: true, message: 'Пожалуйста введите фамилию' }]}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="user-form-block">
                    <Form.Item
                      label="Роль"
                      name={['role', 'type']}
                      rules={[{ required: true, message: 'Пожалуйста выберите роль' }]}
                    >
                      <Select placeholder="Выберите роль" options={ROLE_MAPPING_OPTIONS} />
                    </Form.Item>

                    <Form.Item label="Подтверждён" name="confirmed" valuePropName="checked">
                      <Switch />
                    </Form.Item>

                    <Form.Item label="Заблокирован" name="blocked" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>
                </div>
              </TabPane>
              <TabPane tab="Проекты" key="projects">
                <Form.List name="projects">
                  {(fields, { add, remove }) => (
                    <>
                      <Form.Item label="Проекты">
                        <Select placeholder="Выберите проект" options={projectOptions} onSelect={onProjectSelect} />
                      </Form.Item>
                      {fields.map(({ key, name }) => (
                        <div key={key} className="form-project-row">
                          <Form.Item label="Идентификатор" name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item className="project-form-item" name={[name, 'name']}>
                            <Input disabled />
                          </Form.Item>
                          <Form.Item label="">
                            <MinusCircleOutlined onClick={() => remove(name)} />
                          </Form.Item>
                        </div>
                      ))}
                    </>
                  )}
                </Form.List>
              </TabPane>
            </Tabs>
          </Form>
        </div>
      </Spin>
    </Modal>
  )
}

export default EmployeeModal
